import { z } from 'zod';

/**
 * Contracten van de module audience: wie is de doelgroep van een bericht?
 *
 * Eén vorm voor alles wat een doelgroep kan zijn, en die vorm wordt opgeslagen bij het
 * bericht. Dat is met opzet klein gehouden: hoe meer een doelgroep kan, hoe minder een
 * schrijver zeker weet wie hem straks leest.
 *
 * Het oude platform kon rollen **of** groepen, nooit samen, en een lege keuze betekende
 * iedereen. Dat laatste is hier een eigen vorm (`all`), zodat "ik heb niets gekozen" niet
 * stilzwijgend "iedereen" betekent.
 */

/** Iedereen die in dienst is. Expliciet, niet als gevolg van een leeg filter. */
export const audienceAllSchema = z.object({ type: z.literal('all') });

/**
 * Een combinatie van rollen en groepen.
 *
 * De regels staan in ADR-0035; kort: binnen rollen OF, groepen volgens `groupMatch`, en
 * tussen rollen en groepen EN. "Rol Operator én ploeg Nacht" is dus de operators van de
 * nachtploeg, en niet iedereen die een van beide is.
 */
export const audienceFilterSchema = z.object({
  type: z.literal('filter'),
  roles: z.array(z.guid()).max(50).optional(),
  groups: z.array(z.guid()).max(50).optional(),
  /**
   * Alleen mensen van deze vestigingen (Stap 29).
   *
   * **Binnen sites: OF. Tussen sites en de rest: EN.** "Site B én ploeg Nacht" is de nachtploeg
   * van Kortrijk, en niet iedereen van Kortrijk plus iedereen van de nachtploeg. Dezelfde regel
   * als bij rollen, en om dezelfde reden: een filter toevoegen hoort een doelgroep scherper te
   * maken, niet groter (ADR-0035).
   *
   * Leeg betekent: alle vestigingen. Dat is de standaard, en het verschil met het oude platform:
   * daar ging een bericht naar één organisatie tenzij iemand het doorzette (ADR-0073).
   */
  sites: z.array(z.guid()).max(50).optional(),
  /** `any`: lid van één van de groepen. `all`: lid van alle genoemde groepen. */
  groupMatch: z.enum(['any', 'all']).default('any'),
  /**
   * Een gekozen groep telt met al haar subgroepen (FBK-5, ADR-0092): "Productie" is dan ook Lijn 3
   * en de ploegen eronder. Uitgebreid op het moment van oplossen; de momentopname bij publiceren
   * (ADR-0036) legt de echte ontvangers vast. Weggelaten is uit, zoals vóór FBK-5.
   */
  includeSubgroups: z.boolean().optional(),
  /**
   * Losse mensen erbij (FBK-6, ADR-0093): "Lijn 42 + Geert-Jan + Wout" zonder eerst een groep te
   * maken. **Een unie, geen doorsnede**: deze mensen komen bovenop wat de rest van de filter
   * oplevert, want wie iemand bij naam kiest, bedoelt die persoon. Wie uit dienst is, valt weg.
   */
  persons: z.array(z.guid()).max(200).optional(),
  /**
   * Alleen deze soorten medewerkers (Stap 31). Snijdt, zoals een vestiging: "Ploeg B, alleen
   * vaste medewerkers". Leeg is alle soorten.
   */
  personTypes: z
    .array(z.enum(['employee', 'temporary', 'contractor']))
    .max(3)
    .optional(),
});

/**
 * Een filter zonder rollen en zonder groepen is geen filter, en mag geen sluipweg naar
 * "iedereen" zijn: één vergeten vinkje is anders het verschil tussen vijf en vijfhonderd
 * lezers. Wie iedereen wil bereiken, kiest dat expliciet.
 */
export const audienceSpecSchema = z
  .discriminatedUnion('type', [audienceAllSchema, audienceFilterSchema])
  .refine(audienceHasChoice, {
    message: 'Kies minstens één vestiging, rol, groep of persoon, of kies iedereen.',
  });

/**
 * Is dit een geldige doelgroep: iedereen, of een filter met minstens één keuze?
 *
 * Apart, zodat een scherm dezelfde regel kan toepassen vóór het iets aan de server vraagt. Bij
 * "Kiezen" begint de doelgroep als een lege filter; het voorbeeld vroeg die eerst op, kreeg een
 * 400 en toonde "Het aantal is nu niet op te halen" (testrapport 28 okt.).
 */
export function audienceHasChoice(
  value: { type: 'all' } | Omit<z.infer<typeof audienceFilterSchema>, 'groupMatch'>,
): boolean {
  return (
    value.type === 'all' ||
    (value.roles?.length ?? 0) > 0 ||
    (value.groups?.length ?? 0) > 0 ||
    (value.sites?.length ?? 0) > 0 ||
    (value.personTypes?.length ?? 0) > 0 ||
    (value.persons?.length ?? 0) > 0
  );
}
export type AudienceSpec = z.infer<typeof audienceSpecSchema>;

export const audiencePreviewRequestSchema = z.object({ audience: audienceSpecSchema });
export type AudiencePreviewRequest = z.infer<typeof audiencePreviewRequestSchema>;

/**
 * Wat de schrijver ziet vóórdat hij publiceert.
 *
 * `count` en `description` zijn er altijd; `sample` alleen voor wie medewerkers mag
 * inzien. Een schrijver zonder dat recht hoort te weten hoeveel mensen hij bereikt, niet
 * wie ze zijn.
 */
export const audiencePreviewSchema = z.object({
  count: z.number().int(),
  /** In mensentaal: "Ploeg Nacht én rol Operator". */
  description: z.string(),
  sample: z.array(z.object({ personId: z.guid(), displayName: z.string() })),
  /** Namen weggelaten omdat de aanvrager `directory.person.view` niet heeft. */
  sampleWithheld: z.boolean(),
});
export type AudiencePreview = z.infer<typeof audiencePreviewSchema>;
